import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminEndpoints } from "@/lib/adminApi";
import {
    AdminPageHeader,
    AdminCard,
    StatCard,
    EmptyState,
} from "@/components/admin/AdminUI";
import { ArrowUpRight } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminDashboardPage() {
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        Promise.all([adminEndpoints.stats(), adminEndpoints.leads()])
            .then(([s, l]) => {
                if (cancelled) return;
                setStats(s);
                setLeads(l.slice(0, 10));
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.response?.data?.detail || "Failed to load dashboard");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div data-testid="admin-dashboard-page">
            <AdminPageHeader
                eyebrow="Overview"
                title="Admin Dashboard"
                description="Operational snapshot — leads captured, trainees enrolled, jobs open and applications received."
                testId="admin-dashboard-header"
            />

            {error && (
                <p className="mb-6 border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            )}

            <section
                className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6"
                data-testid="admin-stats-grid"
            >
                <StatCard
                    label="Leads"
                    value={loading ? "…" : stats?.total_leads}
                    testId="stat-total-leads"
                />
                <StatCard
                    label="Trainees"
                    value={loading ? "…" : stats?.total_trainees}
                    testId="stat-total-trainees"
                />
                <StatCard
                    label="Certificates"
                    value={loading ? "…" : stats?.total_certificates}
                    testId="stat-total-certs"
                />
                <StatCard
                    label="Open jobs"
                    value={loading ? "…" : stats?.total_open_jobs}
                    tone="accent"
                    testId="stat-total-jobs"
                />
                <StatCard
                    label="Applications"
                    value={loading ? "…" : stats?.total_applications}
                    testId="stat-total-apps"
                />
                <StatCard
                    label="Apps · 24h"
                    value={loading ? "…" : stats?.applications_last_24h}
                    tone="accent"
                    testId="stat-apps-24h"
                />
            </section>

            <section className="mt-10">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-display text-2xl uppercase tracking-wide">
                        Recent leads
                    </h2>
                    <Link
                        to="/admin/leads"
                        className="inline-flex items-center gap-1 border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold"
                        data-testid="admin-dashboard-view-all-leads"
                    >
                        View all leads
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
                {loading ? (
                    <AdminCard>Loading…</AdminCard>
                ) : leads.length === 0 ? (
                    <EmptyState
                        title="No leads yet"
                        hint="Once prospects submit the contact form, they'll appear here."
                        testId="admin-dashboard-empty-leads"
                    />
                ) : (
                    <div className="overflow-x-auto border border-white/10 bg-white/[0.02]">
                        <table
                            className="min-w-full text-left text-sm"
                            data-testid="admin-dashboard-leads-table"
                        >
                            <thead className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/60">
                                <tr>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">Email</th>
                                    <th className="px-4 py-3">Service</th>
                                    <th className="px-4 py-3">Budget</th>
                                    <th className="px-4 py-3">Received</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((lead) => (
                                    <tr
                                        key={lead.id}
                                        className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]"
                                        data-testid={`dashboard-lead-row-${lead.id}`}
                                    >
                                        <td className="px-4 py-3 font-body text-brand-offwhite">
                                            {lead.name}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/80">
                                            {lead.email}
                                        </td>
                                        <td className="px-4 py-3 font-body text-brand-offwhite/80">
                                            {lead.service_of_interest}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-brand-gold">
                                            {lead.budget_range}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/60">
                                            {formatDate(lead.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
